import { isNil, omitBy } from 'lodash'

import { SelectedDynamicFilterModel } from '../types/models/dynamic-filter'
import {
  GetCatalogFacetedCategoriesArgs,
  GetCatalogFiltersArgs,
  GetCatalogItemsArgs,
  GetCatalogSearchedFiltersArgs,
} from '../types/dtos/api-arg'
import { FilterModel } from '../types/models/filter'
import { SortByOption } from '../constants/filter'

export const getSelectedDynamicFiltersArgs = (
  selectedDynamicFilters?: Array<SelectedDynamicFilterModel>,
): Record<string, string> => {
  if (!selectedDynamicFilters) return {}

  const dynamicParams: Record<string, string> = {}

  selectedDynamicFilters.forEach(({ type, ids }) => {
    dynamicParams[`${type}_ids`] = ids.join(',')
  })

  return dynamicParams
}

export const getCatalogFacetedCategoriesArgsToParams = ({
  query,
  catalogIds,
  priceFrom,
  priceTo,
  currency,
  sortBy,
  time,
  isPopularCatalog,
  catalogFrom,
  selectedDynamicFilters,
  filterSearchCode,
  filterSearchText,
}: GetCatalogFacetedCategoriesArgs) => {
  const staticArgs = {
    search_text: query,
    catalog_ids: catalogIds.join(','),
    price_from: priceFrom,
    price_to: priceTo,
    currency,
    order: sortBy,
    time,
    popular: isPopularCatalog || null,
    catalog_from: catalogFrom,
    filter_search_code: filterSearchCode,
    filter_search_text: filterSearchText,
  }

  return omitBy({ ...staticArgs, ...getSelectedDynamicFiltersArgs(selectedDynamicFilters) }, isNil)
}

export const getCatalogFiltersArgsToParams = ({
  query,
  catalogIds,
  priceFrom,
  priceTo,
  currency,
  sortBy,
  time,
  isPopularCatalog,
  catalogFrom,
  selectedDynamicFilters,
  filterSearchCode,
  filterSearchText,
  filterCode,
}: GetCatalogFiltersArgs) => {
  const staticArgs = {
    search_text: query,
    catalog_ids: catalogIds.join(','),
    price_from: priceFrom,
    price_to: priceTo,
    currency,
    order: sortBy,
    time,
    popular: isPopularCatalog || null,
    catalog_from: catalogFrom,
    filter_search_code: filterSearchCode,
    filter_search_text: filterSearchText,
    filter_code: filterCode,
  }

  return omitBy({ ...staticArgs, ...getSelectedDynamicFiltersArgs(selectedDynamicFilters) }, isNil)
}

export const transformCatalogFiltersModelToParams = ({
  query,
  catalogIds,
  priceFrom,
  priceTo,
  currency,
  sortBy,
  isPopularCatalog,
  catalogFrom,
  disableSearchSaving,
}: Partial<FilterModel>) => ({
  search_text: query,
  catalog_ids: catalogIds?.join(','),
  price_from: priceFrom,
  price_to: priceTo,
  currency,
  order: sortBy || SortByOption.Relevance,
  popular: isPopularCatalog || null,
  catalog_from: catalogFrom,
  disable_search_saving: disableSearchSaving,
})

export const getCatalogItemsArgsToParams = (args: GetCatalogItemsArgs) => {
  const staticArgs = {
    page: args.page,
    per_page: args.perPage,
    time: args.time,
    search_session_id: args.searchSessionId,
    global_search_session_id: args.globalSearchSessionId || null,
    global_catalog_browse_session_id: args.globalCatalogBrowseSessionId || null,
    ...transformCatalogFiltersModelToParams(args),
  }

  return omitBy(
    { ...staticArgs, ...getSelectedDynamicFiltersArgs(args.selectedDynamicFilters) },
    isNil,
  )
}

export const getCatalogSearchedFiltersArgsToParams = ({
  filterSearchCode,
  filterSearchText,
  query,
  catalogIds,
  priceFrom,
  priceTo,
  currency,
  sortBy,
  isPopularCatalog,
  catalogFrom,
  selectedDynamicFilters,
}: GetCatalogSearchedFiltersArgs) => {
  const staticArgs = {
    filter_search_code: filterSearchCode,
    filter_search_text: filterSearchText,
    search_text: query,
    catalog_ids: catalogIds.join(','),
    price_from: priceFrom,
    price_to: priceTo,
    currency,
    order: sortBy,
    popular: isPopularCatalog || null,
    catalog_from: catalogFrom,
  }

  return omitBy({ ...staticArgs, ...getSelectedDynamicFiltersArgs(selectedDynamicFilters) }, isNil)
}
